import { z } from 'zod';
import { TIMEZONES } from '../regions';
/**
 * Owner-side administration of a hospital and the people inside it (SRS 4.2.1,
 * 5.1, 5.3).
 *
 * Everything here is platform staff reaching into a tenant, which is the most
 * dangerous shape of access this system has. Two things are therefore attached
 * to every mutating action, and a UI cannot infer either:
 *
 *   - `totpCode`, a live code from the operator's own authenticator. Being
 *     signed in is not enough; an open console on a desk must not be enough.
 *   - `reason`, free text that lands in the platform audit trail. A hospital
 *     asking "who reset my consultant's password in June, and why" gets an
 *     answer.
 *
 * Anything irreversible adds a third: the hospital's own slug, typed by hand.
 */
/** Every owner action carries a live authenticator code. */
export const totpCodeSchema = z
    .string()
    .regex(/^\d{6}$/, 'Enter the 6-digit code from your authenticator');
export const ownerActionSchema = z.object({
    totpCode: totpCodeSchema,
    reason: z.string().trim().min(5).max(400),
});
export const ownerCloudLoginSchema = ownerActionSchema.extend({ allowed: z.boolean() });
/**
 * Force-logout, with an optional temporary block.
 *
 * `blockMinutes: 0` means sign out only — the user may sign straight back in,
 * which is usually NOT what you want for a runaway client, since it will do
 * exactly that. Capped at 24h on purpose: a longer suspension belongs in the
 * user's active flag, which is visible and auditable, rather than in a cache
 * entry that expires silently with nothing to show for it.
 */
export const ownerForceLogoutSchema = ownerActionSchema.extend({
    blockMinutes: z.coerce.number().int().min(0).max(1440).default(0),
});
/* ------------------------------ owner accounts ----------------------------- */
/** Owner accounts are managed from the console, so a second owner needs no seed. */
export const ownerCreateOwnerSchema = ownerActionSchema.extend({
    name: z.string().trim().min(2).max(160),
    email: z.string().trim().email().max(190),
});
export const ownerUpdateOwnerSchema = ownerActionSchema
    .extend({
    name: z.string().trim().min(2).max(160).optional(),
    email: z.string().trim().email().max(190).optional(),
    isActive: z.boolean().optional(),
})
    .refine((d) => d.name !== undefined || d.email !== undefined || d.isActive !== undefined, 'nothing to update');
/** Live traffic listing window, in minutes. */
export const ownerTrafficQuerySchema = z.object({
    windowMinutes: z.coerce.number().int().min(1).max(10).default(5),
});
/**
 * The owner's switch for the public directory.
 *
 * Step-up 2FA and a reason, like every other owner action: this decides
 * whether a hospital is visible to the whole internet, and turning it off is
 * something the hospital cannot undo for itself.
 */
export const ownerDirectoryVisibilitySchema = ownerActionSchema.extend({
    visible: z.boolean(),
});
/**
 * Branch management from the owner console.
 *
 * Same step-up as every other owner action: adding a site to a live hospital
 * changes what its staff can be assigned to, and that is not a change anybody
 * should be able to make from a console left open on a desk.
 */
export const ownerUpsertBranchSchema = z.object({
    totpCode: totpCodeSchema,
    reason: z.string().trim().min(5).max(400),
    code: z
        .string()
        .trim()
        .toUpperCase()
        .min(2)
        .max(10)
        .regex(/^[A-Z0-9]+$/, 'Use uppercase letters and digits'),
    name: z.string().trim().min(2).max(160),
    address: z.string().trim().max(400).optional(),
    city: z.string().trim().max(80).optional(),
    phone: z.string().trim().max(20).optional(),
    email: z.string().email().optional(),
    timezone: z.enum(TIMEZONES).optional(),
});
export const purgeTenantSchema = z.object({
    totpCode: totpCodeSchema,
    /** The tenant's own slug, typed by hand. A checkbox is too easy to click. */
    confirmSlug: z.string().trim().min(2).max(80),
    reason: z.string().trim().min(10).max(400),
});
/** Wipes clinical records while the hospital itself — staff, roles, catalogues — survives. */
export const purgeClinicalDataSchema = z.object({
    totpCode: totpCodeSchema,
    confirmSlug: z.string().trim().min(2).max(80),
    reason: z.string().trim().min(10).max(400),
});
/**
 * Copying a facility's master data into another (LIMS parity).
 *
 * Setting up a new hospital means re-keying the whole charge master, the test
 * catalogue, the payers and the rest — days of work that the platform already
 * holds for a sister facility. Copying is per group, idempotent on each row's
 * natural code, and reports exactly what it did rather than "done".
 */
export const MASTER_COPY_GROUPS = [
    'specialties',
    'departments',
    'charge-master',
    'tariffs',
    'packages',
    'lab-tests',
    'lab-panels',
    'radiology-types',
    'payers',
    'products',
];
export const MASTER_COPY_GROUP_LABELS = {
    specialties: 'Specialties',
    departments: 'Departments',
    'charge-master': 'Charge master (services and list prices)',
    tariffs: 'Tariffs and negotiated rates',
    packages: 'Fixed-price packages',
    'lab-tests': 'Lab tests (with reference ranges)',
    'lab-panels': 'Lab panels',
    'radiology-types': 'Radiology study types',
    payers: 'Payers (panel and insurance)',
    products: 'Pharmacy formulary',
};
/**
 * What each group needs in place first. A tariff price points at a charge item
 * by code, so copying rates into a facility with no charge master would drop
 * every row as unresolved — the dialog ticks these for you instead.
 */
export const MASTER_COPY_GROUP_DEPENDS_ON = {
    'charge-master': ['departments'],
    tariffs: ['charge-master', 'payers'],
    packages: ['charge-master'],
    'lab-tests': ['charge-master'],
    'lab-panels': ['lab-tests'],
    'radiology-types': ['charge-master'],
};
export const copyMastersSchema = z.object({
    totpCode: totpCodeSchema,
    sourceTenantId: z.string().uuid(),
    groups: z.array(z.enum(MASTER_COPY_GROUPS)).min(1).max(MASTER_COPY_GROUPS.length),
});
/**
 * A facility group: a chain's flagship and its sister sites.
 *
 * One level only, and every member is the same kind of facility — a group
 * exists so a chain's catalogue can be copied between its own sites, and a
 * pharmacy's formulary is no use to a laboratory.
 */
export const updateTenantGroupSchema = z.object({
    totpCode: totpCodeSchema,
    /** The full membership. Anything not listed is detached from the group. */
    childTenantIds: z.array(z.string().uuid()).max(200),
});
/**
 * Exporting a console grid to a file.
 *
 * The client sends what is on screen — the same columns, the same rows, the
 * filters that produced them — so the file cannot disagree with the listing
 * it was taken from.
 */
export const EXPORT_COLUMN_KINDS = [
    'text',
    'mono',
    'number',
    'money',
    'date',
    'datetime',
    'status',
    'list',
];
export const exportGridSchema = z.object({
    format: z.enum(['xlsx', 'pdf']),
    title: z.string().trim().min(1).max(120),
    subtitle: z.string().trim().max(200).optional(),
    meta: z
        .array(z.object({ label: z.string().max(60).optional(), value: z.string().max(200) }))
        .max(20)
        .optional(),
    columns: z
        .array(z.object({
        key: z.string().min(1).max(60),
        label: z.string().min(1).max(60),
        kind: z.enum(EXPORT_COLUMN_KINDS).default('text'),
    }))
        .min(1)
        .max(40),
    rows: z.array(z.record(z.unknown())).max(20_000),
});
export const BRANCH_APPROVAL_STATUSES = ['pending', 'approved', 'rejected'];
export const BRANCH_REQUEST_PAGE_MAX = 100;
export const branchRequestListQuerySchema = z.object({
    status: z.enum(BRANCH_APPROVAL_STATUSES).default('pending'),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(BRANCH_REQUEST_PAGE_MAX).default(50),
});
//# sourceMappingURL=owner.js.map